/**
 * @module actions/UserAccount
 */

import Cookies from 'js-cookie';
import { type Dispatch } from 'redux';
import { createAction } from 'typesafe-actions';

import { type GetState } from 'src/types';

import { type ApiUpdateAccountAction, ApiUpdateAccount } from '../actions/Api';
import { CLEAR_SUCCESS_MESSAGE_DELAY, UserAccountActions } from '../constants/UserAccount';
import { getCookieDomain } from '../lib/ApplicationUtil';
import { getCurrentUsername } from '../lib/ListenerUtils';

const AT_CLASSIC_LOGIN_COOKIE_NAME = 'at';

export const clearSuccessMessage = createAction(
  UserAccountActions.USER_ACCOUNT_CLEAR_SUCCESS_MESSAGE,
)();
export type UserAccountClearSuccessMessageAction = ReturnType<typeof clearSuccessMessage>;

type UserAccountIsEditingParams = $TSFixMe;

interface UserAccountIsEditingPayload {
  isEditing: UserAccountIsEditingParams;
}

export const setIsEditingAccountInfo = createAction(
  UserAccountActions.USER_ACCOUNT_IS_EDITING,
  (isEditing: UserAccountIsEditingParams) => ({ isEditing }),
)<UserAccountIsEditingPayload>();
export type UserAccountIsEditingAction = ReturnType<typeof setIsEditingAccountInfo>;

type UserAccountUpdateAccountParams = $TSFixMe;

interface UserAccountUpdateAccountPayload {
  currentPassword: UserAccountUpdateAccountParams;
}

export const updateAccountInfo = createAction(
  UserAccountActions.USER_ACCOUNT_UPDATE_ACCOUNT,
  (currentPassword: UserAccountUpdateAccountParams) => ({ currentPassword }),
)<UserAccountUpdateAccountPayload>();
export type UserAccountUpdateAccountAction = ReturnType<typeof updateAccountInfo>;

type SetGenderParams = $TSFixMe;

interface SetGenderPayload {
  gender: SetGenderParams;
}

export const setGender = createAction(UserAccountActions.SET_GENDER, (gender: SetGenderParams) => ({
  gender,
}))<SetGenderPayload>();
export type SetGenderAction = ReturnType<typeof setGender>;

export const updateContentSettings =
  (currentPassword: $TSFixMe, settings: $TSFixMe = {}) =>
  (
    dispatch: Dispatch<ApiUpdateAccountAction | UserAccountClearSuccessMessageAction>,
    getState: GetState,
  ): void => {
    const currentUsername = getCurrentUsername(getState());
    const params = Object.assign({ currentUsername, currentPassword }, settings);

    // @ts-expect-error
    return dispatch(ApiUpdateAccount(params)).then(() => {
      window.setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, CLEAR_SUCCESS_MESSAGE_DELAY);
    });
  };

export const clearPasswordForm = createAction(
  UserAccountActions.USER_ACCOUNT_CLEAR_PASSWORD_FORM,
)();
export type UserAccountClearPasswordFormAction = ReturnType<typeof clearPasswordForm>;

export const clearPasswordFormError = createAction(UserAccountActions.CLEAR_ERROR_KEY)();
export type ClearErrorKeyAction = ReturnType<typeof clearPasswordFormError>;

export const clearDeleteListenerError = createAction(
  UserAccountActions.CLEAR_DELETE_LISTENER_ERROR,
)();
export type ClearDeleteListenerErrorAction = ReturnType<typeof clearDeleteListenerError>;

export const clearLogOutAllDevicesError = createAction(
  UserAccountActions.CLEAR_LOG_OUT_ALL_DEVICES_ERROR,
)();
export type ClearLogOutAllDevicesErrorAction = ReturnType<typeof clearLogOutAllDevicesError>;

export const resetLogOutAllDevices = createAction(UserAccountActions.RESET_LOG_OUT_ALL_DEVICES)();
export type ResetLogOutAllDevicesAction = ReturnType<typeof resetLogOutAllDevices>;

const setCookieWithDateAndValue: $TSFixMeFunction = (state, name, value, date) => {
  const domain = getCookieDomain(state);
  Cookies.set(name, value, {
    domain,
    secure: true,
    path: '/',
    expires: date,
  });
};

/*
 * Once a user logs into the classic site, an `at` cookie
 *  with listenerId encoded is set.
 *  This makes it so that a browser will not need to be cookied with pwc (web redesign cookie)
 *    to gain access.
 *  On logout we need to expire this cookie, along with the pwc cookie to prevent non-allowlisted users
 *     from gaining access
 */
export const removeAtCookie = (state: $TSFixMe): void => {
  const date = new Date();
  date.setTime(0);
  setCookieWithDateAndValue(state, AT_CLASSIC_LOGIN_COOKIE_NAME, '', date);
};

export const getAtCookie = (): string | undefined => {
  return Cookies.get(AT_CLASSIC_LOGIN_COOKIE_NAME);
};
